import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSmartFetch, CPLAT_API_ROUTES } from '@/api';
import { useMembershipPreviewMode } from '@/components/membership/MembershipContentLock';
import { useAppSelector } from '@/redux/store';
import {
  LayoutOrderContainer,
  LayoutTabSection,
} from '../orders/_layouts/LayoutOrderContainer';
import type {
  DiagnosisDetail,
  DiagnosisDetailResponse,
} from '@/types/productOptimization';
import DiagnosisDetailSticky from './DiagnosisDetailSticky';
import DiagnosisSection from './DiagnosisSection';
import ScoreWidget from './ScoreWidget';
import GoodsImage from './GoodsImage';
import DeliveryTruckIcon from '@/assets/icons/ProductOptimization/DeliveryTruckIcon.svg?react';
import { mockDiagnosis } from './_mock/mockDiagnoses';

const DiagnosisDetailPanel = () => {
  const [searchParams] = useSearchParams();
  const selectedId = Number(searchParams.get('select'));
  const isPreviewMode = useMembershipPreviewMode();
  const cplatToken = useAppSelector((state) => state.user.CplatToken);

  const leftCardRef = useRef<HTMLElement>(null);
  const [leftHeight, setLeftHeight] = useState(0);

  useEffect(() => {
    if (!leftCardRef.current) return;
    const el = leftCardRef.current;
    const observer = new ResizeObserver(() => {
      setLeftHeight(el.getBoundingClientRect().height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // 음수 id 는 mock 데이터 식별자 (id: -1). API 호출 건너뛰고 mock 렌더.
  const isMockId = selectedId < 0;
  const isPreviewDetail = isPreviewMode || isMockId;

  const { data, isLoading, isError } = useSmartFetch<DiagnosisDetailResponse>(
    CPLAT_API_ROUTES.productOptimizationDetail,
    {
      method: 'POST',
      params: { cplatToken, diagnosisId: selectedId },
      v3: true,
      noSuspense: true,
      config: {
        enabled: !isPreviewMode && !isMockId && !!selectedId && !Number.isNaN(selectedId),
      } as never,
    },
  );

  const detail: DiagnosisDetail | undefined = isPreviewDetail ? mockDiagnosis : data?.data?.[0];

  return (
    <LayoutOrderContainer className="scrollbar overflow-y-auto bg-gr-50">
      <LayoutTabSection>
        <DiagnosisDetailSticky />
      </LayoutTabSection>

      {!isPreviewDetail && isLoading ? (
        <div className="flex h-[200px] items-center justify-center text-gr-500 Body5M14">
          불러오는 중...
        </div>
      ) : isError || !detail ? (
        <div className="flex h-[200px] items-center justify-center text-gr-500 Body5M14">
          진단 결과를 찾을 수 없어요.
        </div>
      ) : (
        <div className="flex flex-col gap-[20px] px-[60px] pb-[72px] pt-[26px]">
          <div className="flex items-stretch gap-[20px]">
            <section
              ref={leftCardRef}
              className="flex flex-1 items-center gap-[12px] rounded-[12px] border border-gr-100 bg-white p-[16px]"
            >
              <GoodsImage
                src={detail.goodsImageUrl}
                alt={detail.goodsName}
                className="h-[166px] w-[166px] shrink-0 rounded-[8px] border border-gr-50 object-cover"
              />
              <div className="flex min-w-0 flex-1 flex-col gap-[10px]">
                <div className="flex flex-col gap-[6px]">
                  <span className="w-fit rounded-[8px] bg-point-100 px-[6px] py-[2px] text-point-500 Caption1M12">
                    {detail.id < 0 ? '예시상품' : '셀러박스'}
                  </span>
                  <span className="line-clamp-2 text-gr-800 Body1S16">
                    {detail.goodsName ?? '-'}
                  </span>
                  <span className="text-gr-600 Body5M14">
                    진단일 : {new Date(detail.diagnosisDate).toLocaleDateString('ko-KR')}
                  </span>
                </div>
                <div className="flex flex-wrap items-end gap-x-[12px] gap-y-[4px]">
                  <div className="flex items-center gap-[4px]">
                    <span className="text-gr-800 Body1S16">
                      {detail.salePrice.toLocaleString()}원
                    </span>
                    {detail.originalPrice > detail.salePrice && (
                      <span className="text-gr-500 line-through Caption3M10">
                        {detail.originalPrice.toLocaleString()}원
                      </span>
                    )}
                  </div>
                  {detail.deliveryFee > 0 && (
                    <div className="flex items-center gap-[6px] text-gr-800 Body2M16">
                      <DeliveryTruckIcon className="h-[20px] w-[20px]" />
                      <span>{detail.deliveryFee.toLocaleString()} 원</span>
                    </div>
                  )}
                </div>
                <div className="flex w-fit max-w-full flex-wrap items-center gap-x-[24px] gap-y-[6px] rounded-[8px] bg-gr-50 p-[10px]">
                  <div className="flex shrink-0 items-center gap-[10px]">
                    <span className="text-gr-600 Body5M14">찜 수</span>
                    <span className="text-gr-700 Body4S14">
                      {detail.wishCount.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-[10px]">
                    <span className="text-gr-600 Body5M14">리뷰 수</span>
                    <span className="text-gr-700 Body4S14">
                      {detail.reviewCount.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-[10px]">
                    <span className="text-gr-600 Body5M14">평점</span>
                    <span className="text-gr-700 Body4S14">{detail.rating.toFixed(1)}</span>
                  </div>
                </div>
              </div>
            </section>

            <ScoreWidget
              score={detail.totalScore}
              maxScore={100}
              size={leftHeight || 198}
            />
          </div>

          <div className="flex flex-col items-stretch gap-[20px] min-[1200px]:flex-row">
            <div className="flex-1">
              <DiagnosisSection title="기본 정보" entries={detail.basicInfoItems} />
            </div>
            <div className="flex flex-1 flex-col gap-[20px]">
              <DiagnosisSection title="상품 상세" entries={detail.productDetailItems} />
              <DiagnosisSection title="판매 관리" entries={detail.salesManagementItems} />
            </div>
          </div>
        </div>
      )}
    </LayoutOrderContainer>
  );
};

export default DiagnosisDetailPanel;
